import { useEffect, useLayoutEffect, type RefObject } from 'react'

const useBrowserLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect

/** The normal loading states share a reserved size. If unusually long content
 * still grows after the reader has passed it, retain the position below it.
 * Native scroll anchoring is accounted for rather than applied a second time. */
export const useRecommendationAnchor = (ref: RefObject<HTMLDivElement>) => {
  useBrowserLayoutEffect(() => {
    const element = ref.current
    if (!element || typeof ResizeObserver === 'undefined') return
    const snapshot = () => {
      const rect = element.getBoundingClientRect()
      return { height: rect.height, width: rect.width, bottom: rect.bottom }
    }
    let previous = snapshot()
    let lastUserScroll = -Infinity
    let pointerDown = false
    const rememberIntent = () => { lastUserScroll = performance.now() }
    const pointerStart = () => { pointerDown = true; rememberIntent() }
    const pointerEnd = () => { pointerDown = false; rememberIntent() }
    const rememberScroll = () => {
      // Keep the previous geometry until ResizeObserver has handled a resize.
      const rect = element.getBoundingClientRect()
      if (Math.abs(rect.height - previous.height) < .5) previous = snapshot()
    }
    const observer = new ResizeObserver(() => {
      const next = snapshot()
      const viewportTop = window.visualViewport?.offsetTop || 0
      const changedSize = Math.abs(next.height - previous.height) >= .5
      const sameWidth = Math.abs(next.width - previous.width) < .5
      if (changedSize && sameWidth && previous.bottom <= viewportTop && !pointerDown && performance.now() - lastUserScroll > 200) {
        // For a native anchor correction next.bottom is already unchanged.
        const shift = next.bottom - previous.bottom
        if (Number.isFinite(shift) && Math.abs(shift) >= .5) {
          const style = document.documentElement.style
          const value = style.getPropertyValue('scroll-behavior')
          const priority = style.getPropertyPriority('scroll-behavior')
          style.setProperty('scroll-behavior', 'auto', 'important')
          window.scrollBy(0, shift)
          if (value) style.setProperty('scroll-behavior', value, priority)
          else style.removeProperty('scroll-behavior')
        }
      }
      previous = snapshot()
    })
    observer.observe(element)
    window.addEventListener('scroll', rememberScroll, { passive: true })
    window.addEventListener('wheel', rememberIntent, { passive: true })
    window.addEventListener('touchmove', rememberIntent, { passive: true })
    window.addEventListener('keydown', rememberIntent)
    window.addEventListener('pointerdown', pointerStart, { passive: true })
    window.addEventListener('pointerup', pointerEnd, { passive: true })
    window.addEventListener('pointercancel', pointerEnd, { passive: true })
    window.addEventListener('blur', pointerEnd)
    return () => {
      observer.disconnect()
      window.removeEventListener('scroll', rememberScroll)
      window.removeEventListener('wheel', rememberIntent)
      window.removeEventListener('touchmove', rememberIntent)
      window.removeEventListener('keydown', rememberIntent)
      window.removeEventListener('pointerdown', pointerStart)
      window.removeEventListener('pointerup', pointerEnd)
      window.removeEventListener('pointercancel', pointerEnd)
      window.removeEventListener('blur', pointerEnd)
    }
  }, [ref])
}
